'use client';

import { unwrapFromResult } from '@shared/utils/helpers/resultWrapper';
import useAuth from '@shared/utils/hooks/useAuth';
import useUser from '@shared/utils/hooks/useUser';
import { useQuery } from '@tanstack/react-query';
import Link from 'next/link';
import { useState } from 'react';
import { client, service } from '../../utils/trpc';
import Button from '../common/Button';
import Modal, { ModalContent, ModalFooter, ModalHeader } from '../HOC/Modal';
type Props = {
  closeHandler: () => void;
};
export const VerificationRequestModal = ({
  closeHandler
}: Props) => {
  const {
    currentUser
  } = useUser();
  const {
    initializeUser
  } = useAuth({
    client,
    service
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fees come from the server; clients never embed the TRON amount as a constant.
  const {
    data: fees
  } = useQuery({
    queryKey: ['verification.fees'],
    queryFn: () => unwrapFromResult(client.verification.getFees.query()),
    staleTime: 60 * 60 * 1000
  });
  const balance = currentUser.tronsBalance;
  const feeTrons = fees ? Number(fees.feeTrons) : null;
  const enough = feeTrons === null ? false : balance >= feeTrons;
  const requestedAt = currentUser.verificationRequestedAt;
  const isPending = !!requestedAt;
  const submit = async () => {
    setSubmitting(true);
    setError(null);
    try {
      await client.verification.request.mutate({});
      // Refresh currentUser so the new tronsBalance + verificationRequestedAt land
      // in client state. No optimistic patch; server is the source of truth.
      await initializeUser();
      closeHandler();
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : 'Failed to submit request';
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };
  const cancel = async () => {
    setSubmitting(true);
    setError(null);
    try {
      await client.verification.cancel.mutate();
      await initializeUser();
      closeHandler();
    } catch (e_0: unknown) {
      const msg_0 = e_0 instanceof Error ? e_0.message : 'Failed to cancel request';
      setError(msg_0);
    } finally {
      setSubmitting(false);
    }
  };
  return <Modal closeHandler={closeHandler}>
      <ModalHeader closeHandler={closeHandler}>
        <h2 className="text-xl font-semibold">Verify your account</h2>
      </ModalHeader>
      <ModalContent>
        <div className="space-y-4 py-2">
          {isPending ? <>
              <span className="inline-flex items-center rounded-full bg-amber-500/10 px-3 py-1 text-xs font-semibold text-amber-500">
                Requested
              </span>
              <p className="text-sm">
                Requested on{' '}
                <strong>{new Date(requestedAt!).toLocaleString()}</strong>.
              </p>
              <p className="text-sm text-textSecondary">
                An admin will reach out to schedule the live call. You can
                cancel and get refunded any time before the session starts.
              </p>
            </> : <>
              <div>
                <p className="text-sm text-textSecondary">
                  During the call we&apos;ll ask you to:
                </p>
                <ul className="mt-2 list-disc space-y-1 pl-5 text-sm text-textSecondary">
                  <li>
                    Show a government-issued ID on camera (we don&apos;t record
                    or save it)
                  </li>
                  <li>
                    Log into a publicly-verified social account and post a
                    session code we provide
                  </li>
                  <li>
                    Submit candidate bios + avatars (
                    {fees?.freeCandidates ?? '…'} free; additional cost more)
                  </li>
                </ul>
              </div>

              <p className="text-sm">
                Cost:{' '}
                <strong>{fees ? feeTrons!.toLocaleString() : '…'} TRONs</strong>{' '}
                (≈ ${fees?.feeDollars ?? '…'}). Refunded if you cancel, are
                denied, or the session times out.
              </p>

              <p className="text-sm text-amber-500">
                If you lose your local copy of approved bios/avatars we cannot
                recover them. You&apos;ll need to re-verify.
              </p>

              <p className="text-sm">
                Your balance: <strong>{balance.toLocaleString()} TRONs</strong>
              </p>
            </>}

          {error && <p className="text-sm text-destructive">{error}</p>}
        </div>
      </ModalContent>
      <ModalFooter>
        <Button text="Close" variant="SECONDARY" onClick={closeHandler} disabled={submitting} />
        {isPending ? <Button text={submitting ? 'Cancelling…' : 'Cancel Request'} variant="DANGER" onClick={cancel} disabled={submitting} /> : enough && fees ? <Button text={submitting ? 'Submitting…' : `Pay ${feeTrons!.toLocaleString()} TRONs`} variant="PRIMARY" onClick={submit} disabled={submitting} /> : <Link href="/products" className="inline-flex cursor-pointer items-center justify-center whitespace-nowrap rounded-full border border-solid border-transparent bg-primary px-6 py-2.5 font-emphasis text-white no-underline transition-all duration-300 hover:bg-indigo-400">
            Top up TRONs
          </Link>}
      </ModalFooter>
    </Modal>;
};